import { useState, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const CATEGORIES = [
  { value: 'irrelevant', label: 'Irrelevant content' },
  { value: 'duplicate', label: 'Duplicate news' },
  { value: 'inaccurate_summary', label: 'Inaccurate summary' },
  { value: 'missing_info', label: 'Missing information' },
  { value: 'feature_broken', label: 'Feature not working' },
  { value: 'other', label: 'Other' },
];

interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  newsId?: string;
}

export function FeedbackDialog({ open, onOpenChange, newsId }: FeedbackDialogProps) {
  const { user } = useAuth();
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState(user?.email ?? '');
  const [pageUrl, setPageUrl] = useState('');
  const [relatedItemId, setRelatedItemId] = useState(newsId ?? '');
  const [submitting, setSubmitting] = useState(false);
  const lastSubmitRef = useRef(0);

  // Capture URL when dialog opens
  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      setPageUrl(window.location.href);
      setEmail(user?.email ?? '');
      setRelatedItemId(newsId ?? '');
    }
    onOpenChange(isOpen);
  };

  const handleSubmit = async () => {
    if (!category || !description.trim()) return;
    if (!user && !email.trim()) return;

    // Prevent double-click (debounce 2s)
    const now = Date.now();
    if (now - lastSubmitRef.current < 2000) return;
    lastSubmitRef.current = now;

    setSubmitting(true);
    try {
      const { error } = await supabase.from('feedback' as any).insert({
        user_id: user?.id ?? null,
        email: email.trim() || null,
        category,
        page_url: pageUrl,
        news_id: relatedItemId.trim() || null,
        description: description.trim(),
      });
      if (error) throw error;
      toast.success('Feedback submitted. Thank you for helping us improve.');
      onOpenChange(false);
      setCategory('');
      setDescription('');
      setRelatedItemId('');
    } catch (err: any) {
      toast.error(err.message || 'Failed to submit feedback.');
    } finally {
      setSubmitting(false);
    }
  };

  const isValid = !!category && !!description.trim() && (!!user || !!email.trim());

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Report Issue</DialogTitle>
          <DialogDescription>
            Help us improve PharmaRadar by reporting issues you encounter.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="fb-category">Category <span className="text-destructive">*</span></Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger id="fb-category">
                <SelectValue placeholder="Select a category..." />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="fb-url">Related URL</Label>
            <Input
              id="fb-url"
              value={pageUrl}
              onChange={(e) => setPageUrl(e.target.value)}
              className="text-muted-foreground"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="fb-item-id">Related Item ID (optional)</Label>
            <Input
              id="fb-item-id"
              placeholder="e.g. news article ID"
              value={relatedItemId}
              onChange={(e) => setRelatedItemId(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="fb-desc">Description <span className="text-destructive">*</span></Label>
            <Textarea
              id="fb-desc"
              placeholder="Describe the issue you encountered..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="fb-email">
              Email {!user && <span className="text-destructive">*</span>}
            </Label>
            <Input
              id="fb-email"
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid || submitting}>
            {submitting ? 'Submitting...' : 'Submit Feedback'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
